<template>
  <div class="w-96 max-h-[420px]">
    <NText variant="caption1" weight="bold">
      {{ t('symbol.recent_broker_change_requests') }}
    </NText>

    <div
      v-if="isLoadingBrokerChanges"
      class="w-full mt-1 flex items-center justify-center"
    >
      <NLoading class="text-brand-primary" />
    </div>

    <div v-else class="max-h-[27rem] h-full mt-1 flex flex-col">
      <NTable
        v-if="brokerChanges.length"
        ref="loadMoreRef"
        :data="brokerChanges"
        cell-padding="small"
        :striped="false"
        :column-splitter="false"
        fixed-header
        class="mt-1 max-h-96 basis-[fit-content]"
      >
        <NTableColumn v-slot="{ row }">
          <NegarInfoWithBorderIcon
            size="20"
            class="cursor-pointer"
            @click="showBrokerChangesDialog(row)"
          />
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="t('modules.portfolio.symbol')"
          prop="instrumentNames"
          sortable
          align="text-right"
        >
          <NText :key="row.instrumentName" variant="caption1">
            {{ row.instrumentName }}
          </NText>
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="t('symbol.broker_change_request_time')"
          prop="requestEntryDate"
          sortable
          align="text-right"
        >
          <NText variant="caption1">
            {{ creationTime(row.requestDate) }}
          </NText>
        </NTableColumn>

        <NTableColumn
          v-slot="{ row }"
          :label="t('common.status')"
          prop="requestStateId"
          sortable
          align="text-right"
        >
          <NText
            :key="row.requestState"
            variant="caption1"
            :class="{
              'text-brand-primary':
                BROKER_CHANGES_REQUEST_STATUS_ID.done.includes(
                  row.requestStateId
                ),
              'text-critical-primary':
                BROKER_CHANGES_REQUEST_STATUS_ID.failed.includes(
                  row.requestStateId
                ),
              'text-neutral-primary':
                BROKER_CHANGES_REQUEST_STATUS_ID.canceled.includes(
                  row.requestStateId
                ),
            }"
          >
            {{ row.requestState }}
          </NText>
        </NTableColumn>

        <template #hoveredElements="{ row }">
          <NegarTrashLightIcon
            v-if="isBrokerChangeRequested(row.requestStateId)"
            ref="deleteBrokerChangeButtonRef"
            size="20"
            class="text-critical-primary ml-2 cursor-pointer"
            @click="handleOpenDeleteDialog(row)"
          />
        </template>
      </NTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import { NegarTrashLightIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import {
  useGetBrokerChangeInfiniteQuery,
  BROKER_CHANGES_REQUEST_STATUS_ID,
  useDeleteBrokerChangeRequestQuery,
  SYMBOL_QUERY_KEYS,
} from '@core/symbol';
import { GetBrokerChangeItem } from '@libs/api-reference';
import { useQueryClient } from '@libs/use';

import {
  DeleteBrokerChangeForm,
  BrokerChangeItemDialog,
} from '@/modules/symbol';

const selectedBrokerChangeSymbolName = ref();
const deleteBrokerChangeButtonRef = ref<HTMLElement>();

const toast = useToast();
const { t, d } = useI18n();
const { add: addDialog, remove: removeDeleteDialog } = useDialog();
const queryClient = useQueryClient();

const { brokerChanges, isLoadingBrokerChanges, loadMoreRef } =
  useGetBrokerChangeInfiniteQuery();

const { deleteBrokerChangeRequest, isDeletingBrokerChangeRequest } =
  useDeleteBrokerChangeRequestQuery({
    onSuccess: () => {
      queryClient.invalidateQueries(SYMBOL_QUERY_KEYS.brokerChange);

      removeDeleteDialog();

      toast.neutral({
        message: t('symbol.broker_change_cancel_broker_done', {
          name: selectedBrokerChangeSymbolName.value,
        }),
        duration: 3000,
      });

      selectedBrokerChangeSymbolName.value = null;
    },
  });

const isBrokerChangeRequested = (requestStateId: number) => {
  return requestStateId === 1;
};

const handleOpenDeleteDialog = (brokerChangeItem: GetBrokerChangeItem) => {
  selectedBrokerChangeSymbolName.value = brokerChangeItem.instrumentName;

  addDialog({
    title: t('symbol.broker_change_cancel_request_title'),
    bodyClass: '!w-1/5',
    referenceElementRef: deleteBrokerChangeButtonRef,
    placement: 'top',
    content: computed(() => ({
      component: DeleteBrokerChangeForm,
      props: {
        requestId: brokerChangeItem.requestId,
        isDeletingBrokerChangeRequest: isDeletingBrokerChangeRequest.value,
        onDelete: deleteBrokerChangeRequest,
        onClose: removeDeleteDialog,
      },
    })),
  });
};

const creationTime = (date) =>
  `${d(date, 'shortDate')} ,
    ${d(date, 'shortTime')}`;

const showBrokerChangesDialog = (item: GetBrokerChangeItem) => {
  addDialog({
    title: t('misc.requestDetails'),
    content: computed(() => ({
      component: BrokerChangeItemDialog,
      props: { item },
    })),
  });
};
</script>
